import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Shield, Users, Save, RotateCcw, Info, Loader2 } from 'lucide-react';
import {
  fetchPermissionsMatrix,
  updateRolePermissions,
  clearSaveStatus,
  updateLocalRolePermissions,
  selectRoles,
  selectScreens,
  selectIsLoadingMatrix,
  selectIsSaving,
  selectSaveSuccess,
  selectSaveError,
} from '../../../features/rbac/rbacSlice';

export default function RoleManagement() {
  const dispatch = useDispatch();
  
  // Redux state
  const roles = useSelector(selectRoles);
  const screens = useSelector(selectScreens);
  const isLoadingMatrix = useSelector(selectIsLoadingMatrix);
  const isSaving = useSelector(selectIsSaving);
  const saveSuccess = useSelector(selectSaveSuccess);
  const saveError = useSelector(selectSaveError);

  // Track which roles have unsaved changes
  const [modifiedRoles, setModifiedRoles] = useState(new Set());
  // Store original permissions for reset functionality
  const [originalPermissions, setOriginalPermissions] = useState({});

  // Fetch permissions matrix on mount
  useEffect(() => {
    dispatch(fetchPermissionsMatrix());
  }, [dispatch]);

  // Store original permissions when roles are loaded
  useEffect(() => {
    if (roles.length > 0) {
      const original = {};
      roles.forEach(role => {
        original[role.id] = JSON.parse(JSON.stringify(role.permissions));
      });
      setOriginalPermissions(original);
      setModifiedRoles(new Set());
    }
  }, [roles.length]); // Only run when roles are first loaded

  // Clear save status after showing
  useEffect(() => {
    if (saveSuccess || saveError) {
      const timer = setTimeout(() => {
        dispatch(clearSaveStatus());
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [saveSuccess, saveError, dispatch]);

  // Refresh matrix after successful save
  useEffect(() => {
    if (saveSuccess) {
      // Update original permissions after successful save
      const updatedOriginal = {};
      roles.forEach(role => {
        updatedOriginal[role.id] = JSON.parse(JSON.stringify(role.permissions));
      });
      setOriginalPermissions(updatedOriginal);
      setModifiedRoles(new Set());
    }
  }, [saveSuccess]);

  const handlePermissionChange = useCallback((roleId, screenId, permissionType) => {
    const role = roles.find(r => r.id === roleId);
    if (!role || !role.isEditable) return;

    const currentValue = role.permissions[screenId]?.[permissionType] || false;
    
    // Update local state immediately for responsive UI
    dispatch(updateLocalRolePermissions({
      roleId,
      screenId,
      permissionType,
      value: !currentValue,
    }));

    // Mark this role as modified
    setModifiedRoles(prev => new Set(prev).add(roleId));
  }, [roles, dispatch]);

  const handleSaveRole = async (roleId) => {
    const role = roles.find(r => r.id === roleId);
    if (!role) return;

    dispatch(updateRolePermissions({
      roleId,
      permissions: role.permissions,
    }));
  };

  const handleResetRole = (roleId) => {
    const original = originalPermissions[roleId];
    if (!original) return;

    // Reset each permission to original value
    const role = roles.find(r => r.id === roleId);
    if (!role) return;

    Object.keys(original).forEach(screenId => {
      if (original[screenId].read !== role.permissions[screenId]?.read) {
        dispatch(updateLocalRolePermissions({
          roleId,
          screenId,
          permissionType: 'read',
          value: original[screenId].read,
        }));
      }
      if (original[screenId].write !== role.permissions[screenId]?.write) {
        dispatch(updateLocalRolePermissions({
          roleId,
          screenId,
          permissionType: 'write',
          value: original[screenId].write,
        }));
      }
    });

    // Remove from modified set
    setModifiedRoles(prev => {
      const next = new Set(prev);
      next.delete(roleId);
      return next;
    });
  };

  const getTotalPermissions = (role) => {
    let read = 0, write = 0;
    if (role.permissions) {
      Object.values(role.permissions).forEach(perm => {
        if (perm.read) read++;
        if (perm.write) write++;
      });
    }
    return { read, write, total: screens.length };
  };

  // Loading state
  if (isLoadingMatrix && roles.length === 0) {
    return (
      <div className="role-management">
        <div className="role-management__loading">
          <Loader2 size={32} className="spin" />
          <p>Loading permissions matrix...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="role-management">
      {/* Header */}
      <div className="role-management__header">
        <div>
          <h3 className="role-management__title">Role Management</h3>
          <p className="role-management__subtitle">
            Define screen-level permissions for each role. Users inherit permissions based on their assigned role.
          </p>
        </div>
      </div>

      {/* Save Status */}
      {saveSuccess && (
        <div className="api-status api-status__success">
          ✓ Permissions saved successfully
        </div>
      )}
      {saveError && (
        <div className="api-status api-status__error">
          ✕ {saveError}
        </div>
      )}

      {/* Info Banner */}
      <div className="permission-info-banner">
        <Info size={18} />
        <div>
          <strong>Permission Types:</strong>
          <span><strong>Read</strong> - View and access the screen</span>
          <span><strong>Write</strong> - Create, edit, and delete data (requires Read permission)</span>
        </div>
      </div>

      {/* Roles Permission Table */}
      {roles.length > 0 && screens.length > 0 && (
        <div className="permission-table-container">
          <table className="permission-table">
            <thead>
              <tr>
                <th className="permission-table__screen-header">Screen</th>
                {roles.map(role => (
                  <th key={role.id} className="permission-table__role-header" colSpan={2}>
                    <div className="role-header-content">
                      <Shield size={16} />
                      <span className="role-header-name">{role.name}</span>
                      <span className="role-header-users">
                        <Users size={12} />
                        {role.userCount || 0}
                      </span>
                    </div>
                    <div className="role-header-permissions">
                      <span>Read</span>
                      <span>Write</span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {screens.map(screen => (
                <tr key={screen.id}>
                  <td className="permission-table__screen-cell">
                    <div className="screen-info">
                      <span className="screen-name">{screen.name}</span>
                      <span className="screen-path">{screen.path}</span>
                    </div>
                  </td>
                  {roles.map(role => {
                    const permissions = role.permissions?.[screen.id] || { read: false, write: false };
                    return (
                      <React.Fragment key={`${role.id}-${screen.id}`}>
                        <td className="permission-table__checkbox-cell">
                          <label className={`permission-checkbox ${!role.isEditable ? 'permission-checkbox--disabled' : ''}`}>
                            <input
                              type="checkbox"
                              checked={permissions.read}
                              onChange={() => handlePermissionChange(role.id, screen.id, 'read')}
                              disabled={!role.isEditable}
                            />
                            <span className="permission-checkbox__box">
                              {permissions.read && <span className="permission-checkbox__check" />}
                            </span>
                          </label>
                        </td>
                        <td className="permission-table__checkbox-cell">
                          <label className={`permission-checkbox ${!role.isEditable ? 'permission-checkbox--disabled' : ''}`}>
                            <input
                              type="checkbox"
                              checked={permissions.write}
                              onChange={() => handlePermissionChange(role.id, screen.id, 'write')}
                              disabled={!role.isEditable}
                            />
                            <span className="permission-checkbox__box">
                              {permissions.write && <span className="permission-checkbox__check" />}
                            </span>
                          </label>
                        </td>
                      </React.Fragment>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Role Summary Cards with Save Buttons */}
      {roles.length > 0 && (
        <div className="role-summary-cards">
          {roles.map(role => {
            const stats = getTotalPermissions(role);
            const hasChanges = modifiedRoles.has(role.id);
            return (
              <div key={role.id} className="role-summary-card">
                <div className="role-summary-card__header">
                  <Shield size={20} className="role-summary-card__icon" />
                  <div>
                    <h4 className="role-summary-card__name">{role.name}</h4>
                    <p className="role-summary-card__description">{role.description}</p>
                  </div>
                </div>
                <div className="role-summary-card__stats">
                  <div className="role-summary-stat">
                    <span className="role-summary-stat__value">{role.userCount || 0}</span>
                    <span className="role-summary-stat__label">Users</span>
                  </div>
                  <div className="role-summary-stat">
                    <span className="role-summary-stat__value">{stats.read}/{stats.total}</span>
                    <span className="role-summary-stat__label">Read Access</span>
                  </div>
                  <div className="role-summary-stat">
                    <span className="role-summary-stat__value">{stats.write}/{stats.total}</span>
                    <span className="role-summary-stat__label">Write Access</span>
                  </div>
                </div>
                
                {/* Action buttons for editable roles */}
                {role.isEditable ? (
                  <div className="role-summary-card__actions">
                    {hasChanges && (
                      <>
                        <button 
                          className="btn btn--outline btn--sm"
                          onClick={() => handleResetRole(role.id)}
                          disabled={isSaving}
                        >
                          <RotateCcw size={14} />
                          Reset
                        </button>
                        <button 
                          className="btn btn--primary btn--sm"
                          onClick={() => handleSaveRole(role.id)}
                          disabled={isSaving}
                        >
                          {isSaving ? (
                            <>
                              <Loader2 size={14} className="spin" />
                              Saving...
                            </>
                          ) : (
                            <>
                              <Save size={14} />
                              Save
                            </>
                          )}
                        </button>
                      </>
                    )}
                    {!hasChanges && (
                      <span className="role-summary-card__status">
                        ✓ No unsaved changes
                      </span>
                    )}
                  </div>
                ) : (
                  <div className="role-summary-card__badge">
                    Full Access (Non-editable)
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
